import React, { useEffect, useRef, useState } from 'react';
import axios from "axios";

// WordPress取得失敗時のフォールバック（現在の固定値と同一）
const FALLBACK_COMPANY = {
  company_name: "株式会社シャルン",
  representative: "我妻 武郎",
  address: "東京都新宿区新宿2丁目12-13 2F\n(台東区に移転予定)",
  established: "2020年10月1日",
  employees: "26人",
  tel: "03-5050-4341",
  email: "info@chaln.co.jp",
};

function BusinessSection() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  // 初期値はフォールバック（＝現在の表示）。fetch完了後にWordPressの値へ差し替え
  const [company, setCompany] = useState(FALLBACK_COMPANY);

  useEffect(() => {
    let hasAppeared = false;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!hasAppeared) {
            setShouldAnimate(true); // 初回のみアニメーション
            hasAppeared = true;
          }
          setVisible(true);
        } else {
          setVisible(false);
          hasAppeared = false; // 完全に画面外に出たら次回もアニメーション
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // WordPressから会社概要を取得（ACFオプションページ + /wp/v2/company）
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await axios.get(
          "https://chaln.co.jp/wp-json/wp/v2/company",
          {
            headers: { "Content-Type": "application/json" },
            timeout: 10000, // 10秒のタイムアウト
          }
        );

        const data = response.data || {};
        // 空でない文字列フィールドのみ採用。改行コードを正規化し前後の空白を除去。
        // 欠損項目はフォールバックを維持
        const cleaned = Object.fromEntries(
          Object.entries(data)
            .filter(([, value]) => typeof value === "string" && value.trim() !== "")
            .map(([key, value]) => [key, value.replace(/\r\n?/g, "\n").trim()])
        );
        setCompany((prev) => ({ ...prev, ...cleaned }));
      } catch (error) {
        console.error("Error fetching company info:", error);
        console.error("Error details:", error.response?.data);
        // エラー時はフォールバック（初期値）のまま表示
      }
    };

    fetchCompany();
  }, []);

  return (
    <section
            id="company"
            ref={ref}
            className={
                `backdrop-blur-md rounded-3xl p-6 md:p-12 shadow-2xl border border-cyan-200/10 hover:border-cyan-300/30 mb-16 max-w-5xl mx-auto ` +
                (visible ? 'opacity-100' : 'opacity-0')
            }
            style={{
                willChange: 'opacity',
                transition: shouldAnimate
                    ? 'opacity 1.2s cubic-bezier(.4,0,.2,1)'
                    : 'none'
            }}
            onTransitionEnd={() => setShouldAnimate(false)}
        >
      <h2 className="text-3xl md:text-5xl font-black tracking-tight drop-shadow-xl text-white mb-6 border-b border-cyan-400/20 pb-2">
        Company
      </h2>
      <div className="flex flex-col items-center">
        <div className="w-full max-w-5xl bg-gray-400/30 to-purple-900/40 rounded-2xl p-8 shadow-xl border border-cyan-400/20 backdrop-blur-lg">
          <dl>
            {/* 会社名 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 items-center py-4 border-b border-cyan-400/20">
              <dt className="font-semibold text-orange-400">会社名</dt>
              <dd className="text-white">{company.company_name}</dd>
            </div>
            {/* 代表 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 items-center py-4 border-b border-cyan-400/20">
              <dt className="font-semibold text-orange-400 ">代表</dt>
              <dd className="text-white">{company.representative}</dd>
            </div>
            {/* 所在地 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 items-center py-4 border-b border-cyan-400/20">
              <dt className="font-semibold text-orange-400">所在地</dt>
              <dd className="text-white" style={{ whiteSpace: 'pre-line' }}>{company.address}</dd>
            </div>
            {/* 設立 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 items-center py-4 border-b border-cyan-400/20">
              <dt className="font-semibold text-orange-400">設立</dt>
              <dd className="text-white">{company.established}</dd>
            </div>
            {/* 従業員数 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 items-center py-4 border-b border-cyan-400/20">
              <dt className="font-semibold text-orange-400">従業員数</dt>
              <dd className="text-white">{company.employees}</dd>
            </div>
            {/* TEL */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 items-center py-4 border-b border-cyan-400/20">
              <dt className="font-semibold text-orange-400">TEL</dt>
              <dd className="text-white">{company.tel}</dd>
            </div>
            {/* E-mail */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 items-center py-4">
              <dt className="font-semibold text-orange-400">E-mail</dt>
              <dd className="text-white break-all">{company.email}</dd>
            </div>
          </dl>
        </div>
        <div className="flex justify-center mt-8">
          <span className="inline-block h-1 w-32 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 rounded-full opacity-70"></span>
        </div>
      </div>
    </section>
  );
}

export default BusinessSection;
